import { get, del, post, put } from 'aws-amplify/api';
import { User } from '../models/user.model';

export const getUsers = async (filters: {
  limit?: number;
  paginationToken?: string;
  filter?: string;
}): Promise<{ items: User[]; paginationToken?: string }> => {
  const limit = filters.limit ?? 60;
  let queryParams = `limit=${limit}`;
  if (filters.paginationToken) {
    queryParams += `&paginationToken=${encodeURIComponent(filters.paginationToken)}`;
  }
  if (filters.filter) {
    queryParams += `&filter=${encodeURIComponent(filters.filter)}`;
  }
  const response = await get({
    apiName: 'users',
    path: `/users?${queryParams}`,
  }).response;
  const json = (await response.body.json()) as any;
  return {
    items: json.items.map((u: any) => mapFromResponse(u)),
    paginationToken: json.paginationToken,
  };
};

export const getUser = async (username: string): Promise<User> => {
  const response = await get({ apiName: 'users', path: `/users/${encodeURIComponent(username)}` }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
};

export const createUser = async (user: {
  email: string;
  givenName?: string;
  familyName?: string;
  position?: string;
  permissions?: string;
  messageAction?: 'SUPPRESS' | 'WELCOME';
}): Promise<User> => {
  const response = await post({
    apiName: 'users',
    path: '/users',
    options: { body: user },
  }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
};

export const updateUser = async (
  username: string,
  attributes: Array<{ name: string; value: string }>
): Promise<void> => {
  await put({
    apiName: 'users',
    path: `/users/${encodeURIComponent(username)}`,
    options: { body: { attributes } },
  }).response;
};

export const deleteUser = async (username: string): Promise<void> => {
  await del({ apiName: 'users', path: `/users/${encodeURIComponent(username)}` }).response;
};

export const enableUser = async (username: string): Promise<void> => {
  await post({
    apiName: 'users',
    path: `/users/${encodeURIComponent(username)}/enable`,
  }).response;
};

export const disableUser = async (username: string): Promise<void> => {
  await post({
    apiName: 'users',
    path: `/users/${encodeURIComponent(username)}/disable`,
  }).response;
};

export const updateUserOrder = async (username: string, order: number): Promise<void> => {
  await updateUser(username, [
    {
      name: 'custom:order',
      value: order.toString(),
    },
  ]);
};

const mapFromResponse = (user: any): User => {
  const orderAttr = user.attributes?.find((attr: any) => attr.name === 'custom:order');
  const order = orderAttr ? parseInt(orderAttr.value, 10) : undefined;

  return {
    username: user.username || '',
    enabled: user.enabled ?? false,
    userStatus: user.userStatus || '',
    userCreateDate: user.userCreateDate ? new Date(user.userCreateDate) : undefined,
    userLastModifiedDate: user.userLastModifiedDate ? new Date(user.userLastModifiedDate) : undefined,
    attributes: user.attributes || [],
    order: isNaN(order as number) ? undefined : order,
  };
};
